/* ═══════════════════════════════════════════════════
   TrackLab v2 — Skeleton loading (team#122)
   Grey placeholders in the shape of what is loading.
   TL.skeleton.load() holds the timing rules so every
   loader behaves the same:
   - the skeleton shows only if loading takes over 150ms
   - once shown it stays at least 300ms
   - after 8s the load counts as failed
   ═══════════════════════════════════════════════════ */

(function () {
  'use strict';

  var SHOW_AFTER = 150;
  var MIN_SHOW = 300;
  var TIMEOUT = 8000;

  var liveRegion = null;
  var busyCount = new Map();   // element → loads in progress (aria-busy)

  TL.skeleton = { SHOW_AFTER: SHOW_AFTER, MIN_SHOW: MIN_SHOW, TIMEOUT: TIMEOUT };

  // One polite live region for the page. The text is set a moment after it
  // changes so screen readers announce it, also on its first use.
  function announce(msg) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      (document.querySelector('.v2-page') || document.body).appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = msg; }, 50);
  }

  function setBusy(el, on) {
    if (!el) return;
    var n = (busyCount.get(el) || 0) + (on ? 1 : -1);
    if (n > 0) {
      busyCount.set(el, n);
      el.setAttribute('aria-busy', 'true');
    } else {
      busyCount.delete(el);
      el.removeAttribute('aria-busy');
    }
  }

  function block(cls, attrs) {
    return '<span class="tl-skel-block ' + cls + '"' + (attrs || '') + '></span>';
  }

  // Up next race row: time chip, name bar (+ second line at More/Full), status bar.
  TL.skeleton.rowHTML = function () {
    return '<div class="tl-skel-row" aria-hidden="true">' + block('sk-time') +
      '<div>' + block('sk-name') + block('sk-sub', ' data-detail="standard"') + '</div>' +
      block('sk-stat') + '</div>';
  };

  // Grey bars in place of text: n letters in the element's own font, so the
  // bars wrap like the real text would. cls adds the real element's class.
  function text(n, cls, attrs) {
    return '<span class="tl-skel-text ' + (cls || '') + '"' + (attrs || '') + '><span>' +
      'xxxxx xxxx xxxxxxx xx xxxxxx xxx '.repeat(Math.ceil(n / 33)).slice(0, n).trim() +
      '</span></span>';
  }

  // The card shows its first 5 runners; the rest sit behind "Show all"
  // (templates/racing/includes/v2_runners_list.html).
  var VISIBLE_RUNNERS = 5;

  // Live race card while it loads: header, track, race pills, top-pick panel
  // and runner rows. It reuses the real card's classes (v2.css), so its sizes
  // follow the card at every width and detail level. No ids, so lookups like
  // getElementById('v2InlineDetail-…') never find it.
  TL.skeleton.cardHTML = function (runners) {
    var count = runners || 8;
    var pill = function (n) { return '<span class="mp">' + text(n) + block('sk-dot m-gloss-dense') + '</span>'; };
    var tag = '<div class="str-item">' + text(6, 'sl') + text(24, 'sv') + '</div>';
    var row = '<div class="runner"><div>' + text(1, 'r-cloth-num') + block('r-num') + '</div>' +
      '<div class="r-info">' + text(14, 'r-name') + text(22, 'r-meta', ' data-detail="standard"') + '</div>' +
      '<div class="r-score">' + text(3, 'num') + '</div></div>';
    return '<div class="race-inline-detail open tl-skel-card" aria-hidden="true">' +
      '<div class="rid-header">' + text(22, 'rid-title') + block('sk-feedback') + '</div>' +
      '<div class="v2-trk"></div>' +
      '<div class="rid-meta" data-detail="standard">' + pill(60) + pill(15) + pill(19) + pill(16) + pill(9) + '</div>' +
      '<div class="detail-panel">' +
        '<div class="dp-top"><div><div class="dp-horse-name">' + block('silk-badge') + text(14) + '</div>' + text(40, 'dp-meta', ' data-detail="deep"') + '</div>' +
        '<div class="dp-score">' + text(3, 'big') + text(17, 'lbl', ' data-detail="deep"') + '</div></div>' +
        '<div class="str-grid" data-detail="standard">' + tag.repeat(5) + '</div>' +
        '<div class="insight-box">' + text(16, 'ib-label') + '<p>' + text(120) + '</p></div>' +
        '<div class="dp-bet-row"><div class="odds-block">' + text(14, 'ob-signal') + text(4, 'ob-frac') +
          text(150, 'ob-detail') + text(15, 'dp-odds-help') + '</div>' +
          '<div class="dp-bet-cta">' + text(14, 'cta-btn') + '</div></div>' +
      '</div>' +
      '<div class="runners-header"><h3>' + text(7) + '</h3>' + text(40, 'sort-by') + '</div>' +
      '<div class="runners-list m-list">' + row.repeat(Math.min(count, VISIBLE_RUNNERS)) + '</div>' +
      (count > VISIBLE_RUNNERS ? text(18, 'show-more-btn') : '') +
      text(10, 'rid-collapse-bar') +
      '</div>';
  };

  TL.skeleton.node = function (html) {
    var temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.firstChild;
  };

  /**
   * Wait for `request` (a Promise) behind a skeleton.
   *   opts.show()  inserts the skeleton and returns its node (or null for
   *                none). Called only if the request is still pending after
   *                150ms. Optional.
   *   opts.busy    element that gets aria-busy="true" while the skeleton shows.
   *   opts.label   announced once when the skeleton shows ("Loading race").
   * Resolves with { value, skeleton } and rejects with { error, skeleton },
   * after 8s at the latest. The skeleton (or null) is still in the page, so
   * the caller can swap it for the content, or a message, in one step.
   */
  TL.skeleton.load = function (request, opts) {
    return new Promise(function (resolve, reject) {
      var skeleton = null;
      var shownAt = 0;
      var settled = false;

      var showTimer = setTimeout(function () {
        skeleton = opts.show ? opts.show() : null;
        if (!skeleton) return;
        shownAt = Date.now();
        setBusy(opts.busy, true);
        if (opts.label) announce(opts.label);
      }, SHOW_AFTER);
      var timeoutTimer = setTimeout(function () {
        finish(reject, { error: new Error('Timed out'), skeleton: skeleton });
      }, TIMEOUT);

      function finish(settle, result) {
        if (settled) return;
        settled = true;
        clearTimeout(showTimer);
        clearTimeout(timeoutTimer);
        var wait = skeleton ? MIN_SHOW - (Date.now() - shownAt) : 0;
        var done = function () {
          if (skeleton) setBusy(opts.busy, false);
          settle(result);
        };
        if (wait > 0) setTimeout(done, wait);
        else done();
      }

      request.then(
        function (value) { finish(resolve, { value: value, skeleton: skeleton }); },
        function (error) { finish(reject, { error: error, skeleton: skeleton }); }
      );
    });
  };

  TL.skeleton.remove = function (node) {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  };

  // team#129: a silk box (.rc-silk, .res-silk, .silk-badge) shimmers while its
  // image loads, with the load() timing rules. The box keeps its size; the
  // image is hidden until the shimmer goes, then shows as today (load or
  // error fallback). No announcement: silks are decorative (alt="").
  TL.skeleton.silk = function (img) {
    var box = img && img.parentNode;
    if (!box || box.nodeType !== 1 || (img.complete && img.getAttribute('src'))) return;
    var loaded = new Promise(function (resolve) {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
    // A newer image in the same box (results spotlight) owns the shimmer then.
    var off = function (r) {
      if (r.skeleton && img.parentNode === box) box.classList.remove('tl-skel-silk');
    };
    TL.skeleton.load(loaded, {
      show: function () {
        if (img.complete && img.getAttribute('src')) return null;
        box.classList.add('tl-skel-silk');
        return box;
      },
    }).then(off, off);
  };

  var SILKS = '.rc-silk > img, .res-silk > img';

  // Silks in the page HTML (loading="lazy"). A box starts its timer when it
  // scrolls into view, so silks further down the page that the browser has
  // not fetched yet do not use up their 8s off screen.
  TL.skeleton.watchSilks = function (root) {
    var imgs = (root || document).querySelectorAll(SILKS);
    if (!imgs.length) return;
    if (typeof IntersectionObserver !== 'function') {
      imgs.forEach(TL.skeleton.silk);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        TL.skeleton.silk(e.target.querySelector('img'));
      });
    });
    imgs.forEach(function (img) { io.observe(img.parentNode); });
  };

  var _onReady = function () { TL.skeleton.watchSilks(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _onReady);
  } else {
    _onReady();
  }
})();
